import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Flex } from '@optiaxiom/react';
import { useDecision } from '@optimizely/react-sdk';

import { Portal } from '@frontend/components';
import { Sidebar } from '@frontend/components/sidebar/Sidebar';
import { useInstanceId, useNamespacedNavigate } from '@frontend/hooks';
import { AgentExecutionItem } from '@frontend/types/agentExecution';
import { WorkflowAgentExecution } from '@frontend/types/workflow-agent';

import { useAgentExecutions } from '../../hooks/useAgentExecutions';
import { useGetWorkflowAgentExecution } from '../../hooks/useWorkflowAgent';
import { WorkflowExecutionContent } from '../workflow-execution';

import { ExecutionDetailsSidebar } from './ExecutionDetailsSidebar';
import { ExecutionsTable } from './ExecutionsTable';

// Convert WorkflowAgentExecution to AgentExecutionItem format
const convertToAgentExecutionItem = (execution: WorkflowAgentExecution): AgentExecutionItem => {
  return {
    id: execution.id,
    workflow_agent_id: execution.workflow_agent_id,
    status: execution.status as AgentExecutionItem['status'],
    start_time: execution.start_time,
    end_time: execution.end_time || undefined,
    trigger_id: execution.trigger_id,
    user_id: execution.user_id,
    links: {
      self: execution.links.self,
      workflow_agent: execution.links.workflow_agent,
    },
  };
};

type AgentExecutionsTabProps = {
  agentId?: string;
  backHref?: string;
};

export function AgentExecutionsTab({ agentId, backHref }: AgentExecutionsTabProps) {
  const { executionId } = useParams<{ executionId: string }>();
  const navigate = useNamespacedNavigate();
  const { instanceId } = useInstanceId();
  const [workflowAgentFeature] = useDecision('workflow-agent');
  const [selectedExecution, setSelectedExecution] = useState<AgentExecutionItem | null>(null);
  const [workflowExecution, setWorkflowExecution] = useState<AgentExecutionItem | null>(null);
  const [isPortalOpen, setIsPortalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const { data, isPending, error } = useAgentExecutions({
    instanceId: instanceId || '',
    agentId,
    page: currentPage,
    pageSize,
  });

  // Fetch execution by ID if URL param is present
  const { data: executionById, isFetching: isFetchingById } =
    useGetWorkflowAgentExecution(executionId);

  // Convert fetched execution to AgentExecutionItem format
  const executionFromUrl = useMemo(() => {
    if (!executionById) return null;
    return convertToAgentExecutionItem(executionById);
  }, [executionById]);

  // Auto-open portal when ID is in URL
  useEffect(() => {
    if (executionId && executionFromUrl && !isFetchingById) {
      setWorkflowExecution(executionFromUrl);
      setIsPortalOpen(true);
    }
  }, [executionId, executionFromUrl, isFetchingById]);

  const handleExecutionSelect = (execution: AgentExecutionItem) => {
    setSelectedExecution(execution);
  };

  const handleCloseSidebar = () => {
    setSelectedExecution(null);
  };

  const handleWorkflowExecutionView = (execution: AgentExecutionItem) => {
    if (!workflowAgentFeature.enabled) {
      return;
    }
    setWorkflowExecution(execution);
    setIsPortalOpen(true);
  };

  const navigateBack = useCallback(() => {
    if (backHref) {
      return navigate(backHref);
    }
    return navigate(-1);
  }, [backHref, navigate]);

  const handleClosePortal = () => {
    setIsPortalOpen(false);
    setWorkflowExecution(null);
    // Navigate back to executions list if we came from a direct URL
    if (executionId) {
      navigateBack();
    }
  };

  // Filter executions based on feature flag
  const filteredExecutions = useMemo(() => {
    if (!data?.items) return [];

    if (!workflowAgentFeature.enabled) {
      // Filter out workflow executions when feature flag is disabled
      return data.items.filter(execution => !execution.workflow_agent_id);
    }

    return data.items;
  }, [data?.items, workflowAgentFeature.enabled]);

  return (
    <Flex h="full" w="full">
      <ExecutionsTable
        executions={filteredExecutions}
        pagination={data?.pagination}
        isLoading={isPending}
        error={error}
        onExecutionSelect={handleExecutionSelect}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
        onWorkflowExecutionView={handleWorkflowExecutionView}
      />

      <Sidebar open={!!selectedExecution} dock="right" initialWidth={500}>
        {selectedExecution && (
          <ExecutionDetailsSidebar execution={selectedExecution} onClose={handleCloseSidebar} />
        )}
      </Sidebar>

      <Portal isOpen={isPortalOpen} onClose={handleClosePortal}>
        {workflowExecution && workflowAgentFeature.enabled && (
          <WorkflowExecutionContent execution={workflowExecution} onClose={handleClosePortal} />
        )}
      </Portal>
    </Flex>
  );
}
